import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as DuplicateIcon,
  ErrorOutline as FailedIcon,
  HelpOutline as PossibleIcon,
  Upload as UploadIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import wineListImportService, { extractImportError } from '../../../../services/wineListImportService';
import type {
  ImportMatchStatus,
  ImportPreviewItem,
  ImportPreviewResponse,
  ImportResolution,
  ImportResolutionAction,
  ImportResult,
  ImportResultItem,
} from '../../../../types/import';

// ── Step labels ───────────────────────────────────────────────────────────────

const IMPORT_STEPS = ['Upload File', 'Review & Resolve', 'Import Summary'];

// ── Match status display config ───────────────────────────────────────────────

const STATUS_CONFIG: Record<
  ImportMatchStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'error'; icon: React.ReactNode; description: string }
> = {
  EXACT: {
    label: 'Exact Match',
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />,
    description: 'Wine and vintage matched precisely. Will be added automatically.',
  },
  POSSIBLE: {
    label: 'Possible Match',
    color: 'warning',
    icon: <PossibleIcon fontSize="small" />,
    description: 'Wine name matched but vintage is ambiguous. Select the correct wine or create a new entry.',
  },
  NONE: {
    label: 'No Match — Create New',
    color: 'default',
    icon: <WarningIcon fontSize="small" />,
    description: 'No existing wine found. Confirm to create a placeholder wine entry marked for review.',
  },
  DUPLICATE: {
    label: 'Duplicate in File',
    color: 'error',
    icon: <DuplicateIcon fontSize="small" />,
    description: 'This wine and vintage appears more than once in the uploaded file. Keep one or skip both.',
  },
  FAILED: {
    label: 'Invalid Row',
    color: 'error',
    icon: <FailedIcon fontSize="small" />,
    description: 'Row could not be parsed. No action possible — will be skipped.',
  },
};


// ── Helpers ───────────────────────────────────────────────────────────────────

const KNOWN_MATCH_STATUSES: ImportMatchStatus[] = ['EXACT', 'POSSIBLE', 'NONE', 'DUPLICATE', 'FAILED'];

function groupPreviewItems(items: ImportPreviewItem[]) {
  const groups = new Map<ImportMatchStatus, ImportPreviewItem[]>();
  for (const status of KNOWN_MATCH_STATUSES) groups.set(status, []);
  for (const item of items) {
    // Unknown matchStatus falls into FAILED so it's always visible in the UI
    // TODO: Remove fallback once backend matchStatus values are confirmed stable
    const status = groups.has(item.matchStatus) ? item.matchStatus : 'FAILED';
    groups.get(status)!.push(item);
  }
  return groups;
}

function defaultResolution(item: ImportPreviewItem): ImportResolution | null {
  if (item.matchStatus === 'EXACT' && item.candidates[0]) {
    return { action: 'use_existing', selectedWineId: item.candidates[0].wine._id };
  }
  if (item.matchStatus === 'FAILED') {
    return { action: 'skip' };
  }
  return null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ResolutionControlsProps {
  item: ImportPreviewItem;
  resolution: ImportResolution | null;
  onChange: (rowIndex: number, resolution: ImportResolution) => void;
}

const ResolutionControls: React.FC<ResolutionControlsProps> = ({ item, resolution, onChange }) => {
  const theme = useTheme();

  if (item.matchStatus === 'EXACT') {
    const wine = item.candidates[0]?.wine;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
        <Typography variant="body2" color="success.main" fontWeight={500}>
          Will use: {wine?.name ?? '—'}
        </Typography>
      </Box>
    );
  }

  if (item.matchStatus === 'FAILED') {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic">
        Row cannot be imported
      </Typography>
    );
  }

  const currentAction = resolution?.action ?? '';

  const handleActionChange = (action: ImportResolutionAction) => {
    if (action === 'use_existing') {
      const firstCandidate = item.candidates[0]?.wine._id;
      onChange(item.rowIndex, { action, selectedWineId: firstCandidate });
    } else {
      onChange(item.rowIndex, { action });
    }
  };

  return (
    <Box>
      <RadioGroup
        value={currentAction}
        onChange={e => handleActionChange(e.target.value as ImportResolutionAction)}
        sx={{ gap: 0.5 }}
      >
        {item.matchStatus === 'POSSIBLE' && item.candidates.map(c => (
          <FormControlLabel
            key={c.wine._id}
            value="use_existing"
            control={
              <Radio
                size="small"
                checked={currentAction === 'use_existing' && resolution?.selectedWineId === c.wine._id}
                onChange={() => onChange(item.rowIndex, { action: 'use_existing', selectedWineId: c.wine._id })}
              />
            }
            label={
              <Typography variant="body2">
                Use: <strong>{c.wine.name}</strong>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  {c.wine.wineName.producer?.name}
                </Typography>
              </Typography>
            }
            sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } }}
          />
        ))}

        {(item.matchStatus === 'NONE' || item.matchStatus === 'DUPLICATE') && (
          <FormControlLabel
            value="create_new"
            control={<Radio size="small" />}
            label={
              <Typography variant="body2">
                Create new wine placeholder <Chip label="needsReview" size="small" sx={{ fontSize: '0.65rem', height: 18, ml: 0.5 }} />
              </Typography>
            }
          />
        )}

        <FormControlLabel
          value="skip"
          control={<Radio size="small" />}
          label={<Typography variant="body2" color="text.secondary">Skip this row</Typography>}
        />
      </RadioGroup>
    </Box>
  );
};

interface PreviewRowProps {
  item: ImportPreviewItem;
  resolution: ImportResolution | null;
  onResolutionChange: (rowIndex: number, resolution: ImportResolution) => void;
}

const PreviewRow: React.FC<PreviewRowProps> = ({ item, resolution, onResolutionChange }) => {
  const theme = useTheme();

  // Guard: importRow must exist before rendering row details.
  // The normalizer in the service filters these out, but this is a belt-and-suspenders check.
  // TODO: Remove if backend guarantees importRow is always present.
  if (!item.importRow) {
    return (
      <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
        <Typography variant="caption" color="error">
          Row {item.rowIndex}: data missing — cannot display
        </Typography>
      </Box>
    );
  }

  const { importRow, warnings } = item;

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Row info */}
        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', flexShrink: 0 }}>
              #{importRow.rowIndex}
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap>
              {importRow.rawName || <em style={{ color: theme.palette.text.disabled }}>—</em>}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {importRow.producerName && (
              <Typography variant="caption" color="text.secondary">{importRow.producerName}</Typography>
            )}
            {importRow.vintage && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {importRow.vintage}
              </Typography>
            )}
            {importRow.price != null && (
              <Typography variant="caption" color="text.secondary">
                {importRow.price.toFixed(2)}
              </Typography>
            )}
            {importRow.notes && (
              <Tooltip title={importRow.notes}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                  {importRow.notes}
                </Typography>
              </Tooltip>
            )}
          </Box>

          {/* Warnings */}
          {warnings.length > 0 && (
            <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {warnings.map((w, i) => (
                <Typography key={i} variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningIcon sx={{ fontSize: 12 }} />
                  {w}
                </Typography>
              ))}
            </Box>
          )}
        </Box>

        {/* Resolution controls */}
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <ResolutionControls
            item={item}
            resolution={resolution}
            onChange={onResolutionChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

interface PreviewSectionProps {
  status: ImportMatchStatus;
  items: ImportPreviewItem[];
  resolutions: Map<number, ImportResolution>;
  onResolutionChange: (rowIndex: number, resolution: ImportResolution) => void;
}

const PreviewSection: React.FC<PreviewSectionProps> = ({ status, items, resolutions, onResolutionChange }) => {
  const theme = useTheme();
  const config = STATUS_CONFIG[status];

  if (items.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: alpha(theme.palette.divider, 0.04),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Chip
          icon={config.icon as React.ReactElement}
          label={`${config.label} (${items.length})`}
          size="small"
          color={config.color}
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        />
        <Typography variant="caption" color="text.secondary">
          {config.description}
        </Typography>
      </Box>

      {items.map(item => (
        <PreviewRow
          key={item.rowIndex}
          item={item}
          resolution={resolutions.get(item.rowIndex) ?? null}
          onResolutionChange={onResolutionChange}
        />
      ))}
    </Box>
  );
};

// ── Result status config ──────────────────────────────────────────────────────

const RESULT_STATUS_COLOR: Record<ImportResultItem['status'], string> = {
  created: '#1a7f37',
  skipped: '#856404',
  failed: '#dc3545',
};

// ── Main component ────────────────────────────────────────────────────────────

const WineListImport: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const { wineListId } = useParams<{ wineListId: string }>();
  const backPath = `/admin/venues/${venueId}/wine-lists/${wineListId}`;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [resolutions, setResolutions] = useState<Map<number, ImportResolution>>(new Map());
  const [result, setResult] = useState<ImportResult | null>(null);
  // Separate loading + error states per operation so banners appear on the right step
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  // ── Grouped preview ─────────────────────────────────────────────────────────

  const grouped = useMemo(
    () => (preview ? groupPreviewItems(preview.items) : new Map<ImportMatchStatus, ImportPreviewItem[]>()),
    [preview],
  );

  // Count unresolved rows that require user action (POSSIBLE, NONE, DUPLICATE)
  const unresolvedCount = useMemo(() => {
    if (!preview) return 0;
    return preview.items.filter(item => {
      if (!['POSSIBLE', 'NONE', 'DUPLICATE'].includes(item.matchStatus)) return false;
      return !resolutions.has(item.rowIndex);
    }).length;
  }, [preview, resolutions]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setPreviewError('');
  };

  const handlePreview = async () => {
    if (!file || !wineListId) return;
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const previewData = await wineListImportService.previewImport(venueId, wineListId, file);
      const initialResolutions = new Map<number, ImportResolution>();
      for (const item of previewData.items) {
        const def = defaultResolution(item);
        if (def) initialResolutions.set(item.rowIndex, def);
      }
      setPreview(previewData);
      setResolutions(initialResolutions);
      setStep(1);
    } catch (err: unknown) {
      setPreviewError(extractImportError(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleResolutionChange = (rowIndex: number, resolution: ImportResolution) => {
    setResolutions(prev => new Map(prev).set(rowIndex, resolution));
  };

  const handleConfirm = async () => {
    if (!preview || !wineListId) return;
    setConfirmLoading(true);
    setConfirmError('');
    try {
      // Merge resolutions into preview items before sending.
      // Rows without an explicit resolution default to 'skip' as a safe fallback.
      const itemsWithResolutions: ImportPreviewItem[] = preview.items.map(item => ({
        ...item,
        resolution: resolutions.get(item.rowIndex) ?? { action: 'skip' },
      }));
      const importResult = await wineListImportService.confirmImport(venueId, wineListId, itemsWithResolutions);
      setResult(importResult);
      setStep(2);
    } catch (err: unknown) {
      // Row-level failures return HTTP 200 with ImportResult.failed > 0 — they are shown
      // in the result screen. This catch only handles request-level failures.
      setConfirmError(extractImportError(err));
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setFile(null);
    setPreview(null);
    setResolutions(new Map());
    setResult(null);
    setPreviewError('');
    setConfirmError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            Wine List
          </Button>
          <Typography variant="h6" fontWeight={600}>Import Wines</Typography>
        </Box>

        <Box sx={{ p: 3 }}>

          {/* Step indicator */}
          <Stepper activeStep={step} sx={{ mb: 4 }}>
            {IMPORT_STEPS.map(label => (
              <Step key={label}>
                <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.8rem' } }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ── Step 0: Upload ──────────────────────────────────────────────── */}
          {step === 0 && (
            <Box>
              {previewError && (
                <Alert severity="error" onClose={() => setPreviewError('')} sx={{ mb: 2, borderRadius: 1 }}>
                  {previewError}
                </Alert>
              )}

              <Box
                sx={{
                  border: `2px dashed ${alpha(theme.palette.primary.main, previewLoading ? 0.15 : 0.3)}`,
                  borderRadius: 2,
                  p: 5,
                  textAlign: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  cursor: previewLoading ? 'not-allowed' : 'pointer',
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: previewLoading ? undefined : alpha(theme.palette.primary.main, 0.6) },
                  opacity: previewLoading ? 0.6 : 1,
                }}
                onClick={() => !previewLoading && fileInputRef.current?.click()}
              >
                <UploadIcon sx={{ fontSize: 44, color: alpha(theme.palette.primary.main, 0.4), mb: 1.5 }} />
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  {file ? file.name : 'Click to choose a file'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  .xlsx or .csv · Maximum 200 rows
                </Typography>
                {file && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                )}
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              {/* Expected columns */}
              <Box sx={{ mt: 2.5, p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.info.main, 0.15)}` }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.75 }}>
                  Expected columns
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {['Wine Name *', 'Producer', 'Vintage', 'Price', 'Notes'].map(col => (
                    <Chip
                      key={col}
                      label={col}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', borderColor: alpha(theme.palette.info.main, 0.3) }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  * Required. All imported wines will be added as <strong>Hidden</strong> and require manual publish.
                </Typography>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  disabled={!file || previewLoading}
                  onClick={handlePreview}
                  startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {previewLoading ? 'Generating Preview…' : 'Generate Preview'}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 1: Preview & Resolve ───────────────────────────────────── */}
          {step === 1 && preview && (
            <Box>
              {confirmError && (
                <Alert severity="error" onClose={() => setConfirmError('')} sx={{ mb: 2, borderRadius: 1 }}>
                  {confirmError}
                </Alert>
              )}

              {/* Summary bar */}
              <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 3, p: 2, backgroundColor: alpha(theme.palette.divider, 0.04), borderRadius: 1.5 }}>
                {[
                  { label: 'Exact', count: preview.exactCount, color: '#1a7f37' },
                  { label: 'Possible', count: preview.possibleCount, color: '#856404' },
                  { label: 'No match', count: preview.noneCount, color: theme.palette.text.secondary },
                  { label: 'Duplicates', count: preview.duplicateCount, color: theme.palette.error.main },
                  { label: 'Failed', count: preview.failedCount, color: theme.palette.error.light },
                ].map(({ label, count, color }) => (
                  <Box key={label} sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>{count}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Empty state — backend returned no rows */}
              {preview.items.length === 0 ? (
                <Box sx={{ py: 8, textAlign: 'center', color: theme.palette.text.secondary }}>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    No rows to review
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    The file was parsed but contained no valid rows. Check the file format and try again.
                  </Typography>
                  <Button variant="outlined" onClick={handleReset} sx={{ textTransform: 'none' }}>
                    Upload a different file
                  </Button>
                </Box>
              ) : (
                <>
                  {unresolvedCount > 0 && (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
                      {unresolvedCount} {unresolvedCount === 1 ? 'row requires' : 'rows require'} a decision before you can confirm.
                    </Alert>
                  )}

                  {/* Grouped sections */}
                  <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                    {(['EXACT', 'POSSIBLE', 'NONE', 'DUPLICATE', 'FAILED'] as ImportMatchStatus[]).map(status => (
                      <PreviewSection
                        key={status}
                        status={status}
                        items={grouped.get(status) ?? []}
                        resolutions={resolutions}
                        onResolutionChange={handleResolutionChange}
                      />
                    ))}
                  </Paper>

                  <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'space-between' }}>
                    <Button
                      variant="text"
                      disabled={confirmLoading}
                      onClick={handleReset}
                      sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
                    >
                      Start over
                    </Button>
                    <Button
                      variant="contained"
                      disabled={unresolvedCount > 0 || confirmLoading}
                      onClick={handleConfirm}
                      startIcon={confirmLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      {confirmLoading
                        ? 'Importing…'
                        : `Confirm Import (${preview.totalRows - preview.failedCount} rows)`}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* ── Step 2: Result Summary ──────────────────────────────────────── */}
          {step === 2 && result && (
            <Box>
              {/* Top-line summary */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                {[
                  { label: 'Created', count: result.created, color: '#1a7f37', bg: alpha('#1a7f37', 0.07) },
                  { label: 'Skipped', count: result.skipped, color: '#856404', bg: alpha('#856404', 0.07) },
                  { label: 'Failed', count: result.failed, color: theme.palette.error.main, bg: alpha(theme.palette.error.main, 0.07) },
                ].map(({ label, count, color, bg }) => (
                  <Paper
                    key={label}
                    variant="outlined"
                    sx={{ px: 3, py: 2, textAlign: 'center', flex: '1 1 100px', borderRadius: 2, backgroundColor: bg, borderColor: 'transparent' }}
                  >
                    <Typography variant="h4" fontWeight={700} sx={{ color }}>{count}</Typography>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                  </Paper>
                ))}
              </Box>

              {/* New records created */}
              {(result.newWinesCreated > 0 || result.newProducersCreated > 0) && (
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={500}>New records were created and marked for review:</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {result.newWinesCreated > 0 && (
                      <Typography variant="body2">· {result.newWinesCreated} new wine {result.newWinesCreated === 1 ? 'entry' : 'entries'} (needsReview)</Typography>
                    )}
                    {result.newProducersCreated > 0 && (
                      <Typography variant="body2">· {result.newProducersCreated} new producer {result.newProducersCreated === 1 ? 'stub' : 'stubs'} (needsReview)</Typography>
                    )}
                  </Box>
                </Alert>
              )}

              {/* Per-row results table */}
              <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden', mb: 3 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', color: theme.palette.text.secondary, width: 48 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', color: theme.palette.text.secondary }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', color: theme.palette.text.secondary }}>Detail</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', color: theme.palette.text.secondary, width: 80 }}>New?</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                No row-level detail available.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                      {result.items.map(item => (
                        <TableRow key={item.rowIndex}>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: theme.palette.text.disabled }}>
                              {item.rowIndex}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: RESULT_STATUS_COLOR[item.status], textTransform: 'capitalize' }}
                            >
                              {item.status}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color={item.reason ? 'text.secondary' : 'text.primary'} sx={{ fontStyle: item.reason ? 'italic' : 'normal' }}>
                              {item.reason ?? item.wineListItemId ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {item.wineCreated && (
                                <Chip label="wine" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 18 }} />
                              )}
                              {item.producerCreated && (
                                <Chip label="producer" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 18 }} />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Divider sx={{ mb: 2.5 }} />

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'space-between' }}>
                <Button variant="outlined" onClick={handleReset} sx={{ textTransform: 'none' }}>
                  Import another file
                </Button>
                <Button variant="contained" onClick={() => navigate(backPath)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Back to Wine List
                </Button>
              </Box>
            </Box>
          )}

        </Box>
      </Paper>
    </Box>
  );
};

export default WineListImport;
